'use client';

import { ViewSettings } from '@api';
import {
  ASSESSMENT_LOG_TABLE_ID,
  MODEL_TABLE_ID,
  RISK_PROFILE_TABLE_ID,
  RISK_TABLE_ID,
  USE_CASE_TABLE_ID,
  GOVERNANCE_EVALUATION_LOG_TABLE_ID,
  DATASET_TABLE_ID,
} from '@components/table/constants';
import { Apis, depaginate } from '@data-access';
import { APIHelper } from '@helpers';
import { action, makeObservable, observable } from 'mobx';

export class ViewSettingsStore {
  viewSettingsById: Record<number, ViewSettings> = {};

  useCaseInventoryId: number | null = null;

  modelInventoryId: number | null = null;

  riskInventoryId: number | null = null;

  datasetInventoryId: number | null = null;

  governanceEvaluationLogInventoryId: number | null = null;

  assessmentLogInventoryId: number | null = null;

  riskProfileTableId: number | null = null;

  constructor() {
    makeObservable(this, {
      viewSettingsById: observable,
      setViewSettingsRecord: action.bound,
      updateViewSettingsRecord: action.bound,
      useCaseInventoryId: observable,
      modelInventoryId: observable,
      riskInventoryId: observable,
      datasetInventoryId: observable,
      assessmentLogInventoryId: observable,
      governanceEvaluationLogInventoryId: observable,
      riskProfileTableId: observable,
      setUseCaseInventoryId: action.bound,
      setModelInventoryId: action.bound,
      setRiskInventoryId: action.bound,
      setDatasetInventoryId: action.bound,
      setAssessmentLogInventoryId: action.bound,
      setGovernanceEvaluationLogInventoryId: action.bound,
      setRiskProfileTableId: action.bound,
      setViewSetting: action.bound,
    });
  }

  setViewSettingsRecord(viewSettings: ViewSettings) {
    this.viewSettingsById = { ...this.viewSettingsById, [viewSettings.id]: viewSettings };
  }

  updateViewSettingsRecord(id: number, patch: Partial<ViewSettings>) {
    const current = this.viewSettingsById[id];
    if (!current) return;
    this.viewSettingsById = { ...this.viewSettingsById, [id]: { ...current, ...patch } };
  }

  setUseCaseInventoryId(data) {
    this.useCaseInventoryId = data;
  }

  setModelInventoryId(data) {
    this.modelInventoryId = data;
  }

  setRiskInventoryId(data) {
    this.riskInventoryId = data;
  }

  setDatasetInventoryId(data) {
    this.datasetInventoryId = data;
  }

  setAssessmentLogInventoryId(data) {
    this.assessmentLogInventoryId = data;
  }

  setGovernanceEvaluationLogInventoryId(data) {
    this.governanceEvaluationLogInventoryId = data;
  }

  setRiskProfileTableId(data) {
    this.riskProfileTableId = data;
  }

  async setViewSetting() {
    let apis: Apis;

    try {
      const config = await APIHelper.createApiConfiguration();
      apis = APIHelper.createApisFromConfig(config);
    } catch (error) {
      console.log(error);
    }

    try {
      const results = await depaginate(page => apis?.api.apiViewSettingsList({ page }));

      results.forEach(viewSettings => {
        this.setViewSettingsRecord(viewSettings);

        switch (viewSettings.tableName) {
          case USE_CASE_TABLE_ID:
            this.setUseCaseInventoryId(viewSettings.id);
            break;
          case MODEL_TABLE_ID:
            this.setModelInventoryId(viewSettings.id);
            break;
          case RISK_TABLE_ID:
            this.setRiskInventoryId(viewSettings.id);
            break;
          case ASSESSMENT_LOG_TABLE_ID:
            this.setAssessmentLogInventoryId(viewSettings.id);
            break;
          case RISK_PROFILE_TABLE_ID:
            this.setRiskProfileTableId(viewSettings.id);
            break;
          case DATASET_TABLE_ID:
            this.setDatasetInventoryId(viewSettings.id);
            break;
          case GOVERNANCE_EVALUATION_LOG_TABLE_ID:
            this.setGovernanceEvaluationLogInventoryId(viewSettings.id);
            break;
          default:
            break;
        }
      });
    } catch (_) {
      // Error intentionally ignored
    }
  }
}

export const viewSettingsStore = new ViewSettingsStore();
